import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlashcardCard from "../../../components/flashcard/FlashcardCard";
import { flashcardApi } from "../../../api/flashcardApi";
import "./ReviewFlashCardNoScore.css";

const MAX_CARDS = 50;

const normalizeCard = (item) => ({
  id: item?.id ?? item?.Id,
  categoryId: item?.categoryId ?? item?.CategoryId,
  frontText: item?.frontText ?? item?.FrontText ?? "",
  backText: item?.backText ?? item?.BackText ?? "",
  transliteration: item?.transliteration ?? item?.Transliteration ?? "",
  usageExample: item?.usageExample ?? item?.UsageExample ?? "",
  voiceUrl: item?.voiceUrl ?? item?.VoiceUrl ?? item?.audioUrl ?? item?.AudioUrl ?? null,
  imageUrl: item?.imageUrl ?? item?.ImageUrl ?? null,
});

export default function ReviewFlashCardNoScore() {
  const location = useLocation();
  const navigate = useNavigate();
  const deskId = location?.state?.id ?? new URLSearchParams(location.search).get("deskId");
  const deskName = location?.state?.name ?? "Category";
  const resumeIndex = Number(location?.state?.resumeIndex ?? 0);

  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadCards = useCallback(async () => {
    if (!deskId) {
      setError("Không tìm thấy category để ôn tập.");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const params = {
        pageNumber: 1,
        pageSize: MAX_CARDS,
        filters: {
          CategoryIds: [String(deskId)],
        },
        isSortDesc: false,
        sortKey: "createdAt",
        seachKey: "",
      };
      const res = await flashcardApi.getTable(params);
      const rawItems = res?.data?.items ?? res?.items ?? [];
      const mapped = rawItems.map(normalizeCard).filter((item) => !!item.id);
      setCards(mapped);
      const startIndex = Number.isInteger(resumeIndex)
        ? Math.min(Math.max(0, resumeIndex), Math.max(0, mapped.length - 1))
        : 0;
      setIndex(startIndex);
    } catch (err) {
      setError(err?.message || "Không tải được danh sách flashcard.");
      setCards([]);
      setIndex(0);
    } finally {
      setIsLoading(false);
    }
  }, [deskId, resumeIndex]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (cards.length === 0) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setIndex((prev) => Math.min(cards.length - 1, prev + 1));
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setIndex((prev) => Math.max(0, prev - 1));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cards.length]);

  const currentCard = cards[index] ?? null;
  const progress = useMemo(() => {
    if (cards.length === 0) return 0;
    return Math.round(((index + 1) / cards.length) * 100);
  }, [cards.length, index]);

  return (
    <div className="container py-4 review-no-score-session">
      <section className="review-no-score-header mb-4">
        <div>
          <h3 className="mb-1">Review by Category No Score</h3>
          <p className="mb-0 text-muted">
            {deskName} • {cards.length} cards (max {MAX_CARDS})
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => navigate("/review/category-no-score")}
        >
          Back
        </button>
      </section>

      <div className="review-no-score-progress mb-4">
        <div className="d-flex justify-content-between small mb-2">
          <span>
            Card {cards.length === 0 ? 0 : index + 1}/{cards.length}
          </span>
          <span>{progress}%</span>
        </div>
        <div className="progress">
          <div className="progress-bar" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      {isLoading && <div className="text-muted">Đang tải flashcards...</div>}
      {!isLoading && error && <div className="alert alert-danger py-2">{error}</div>}

      {!isLoading && !error && cards.length === 0 && (
        <div className="alert alert-info">Category này chưa có flashcard để ôn.</div>
      )}

      {!isLoading && !error && currentCard && (
        <div className="review-no-score-main">
          <div className="d-flex justify-content-center mb-4">
            <FlashcardCard
              card={currentCard}
              detailsState={{
                returnPath: "/review/flashcard-no-score",
                returnState: {
                  id: deskId,
                  name: deskName,
                  resumeIndex: index,
                },
              }}
            />
          </div>

          <div className="review-no-score-controls">
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={index === 0}
              onClick={() => setIndex((prev) => Math.max(0, prev - 1))}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={index >= cards.length - 1}
              onClick={() => setIndex((prev) => Math.min(cards.length - 1, prev + 1))}
            >
              Next
              <i className="bi bi-arrow-right ms-1"></i>
            </button>
          </div>

          <p className="small text-muted mt-3 mb-0 text-center">
            Keyboard: <span className="fw-semibold">←</span> Previous, <span className="fw-semibold">→</span> Next
          </p>
        </div>
      )}
    </div>
  );
}
